// Sourced from a real analysis-service threat model of OWASP WebGoat (doc_id
// 0b91302c-db46-4a20-8060-787a0fceee2a). The threat-model `/content` endpoint returns exactly this
// `model_json` object — the platform forwards the analysis-service body byte-for-byte when the caller
// sends `Accept: application/json` — so this fixture is the wire payload verbatim, used by the demo
// mock and by threat-model stories/tests to present a realistic completed document.
import type { ThreatModelContent } from '../src/utils/api-client/api-schemas'

export const exampleThreatModelContent: ThreatModelContent = {
  system_overview:
    'WebGoat is an OWASP-maintained deliberately insecure Spring Boot web application used to teach web application security through vulnerable lessons. The repository also includes WebWolf, a companion service used by some lessons for mailbox/file-transfer style interactions and request tracing. The application is a browser-driven monolith with server-side MVC, JSON endpoints, a local HSQLDB-backed user/progress store, and lesson-specific filesystem state.',
  exposure: {
    notes:
      'README warns users to keep it offline; Dockerfile and README show both 127.0.0.1-published and custom-host deployments. Baseline impact is lower for default local use, but findings become network-reachable if the operator binds to 0.0.0.0 or publishes container ports.',
    internet_facing: 'partial',
    network_position:
      'default-bind localhost/127.0.0.1 for WebGoat and WebWolf, but Docker/docs support exposing ports 8080/9090 and custom hostnames; exposure depends on deployment',
    deployment_environment: 'Spring Boot monolith run from source, via Docker, or as a standalone JAR',
  },
  actors: [
    {
      name: 'anonymous visitor',
      can_reach:
        'login page, registration, static assets, public GET/POST WebWolf routes, and any lesson/router surface left unauthenticated by configuration',
      trust_level: 'anonymous',
      authenticates_via:
        'none; can reach public/login/registration/static routes and any endpoints explicitly permitAll',
    },
    {
      name: 'authenticated learner',
      can_reach:
        'lesson pages, lesson JSON/AJAX endpoints, user-specific progress/report/menu data, reset/restart actions, uploads, and WebWolf features allowed by its route rules',
      trust_level: 'authenticated',
      authenticates_via: 'Spring Security form login or OAuth2 login with session cookie',
    },
    {
      name: 'privileged/admin lesson actor',
      can_reach:
        'admin-gated lesson flows and any role-checks present in templates/controllers; some endpoints are role-neutral and remain user-scoped rather than admin-scoped',
      trust_level: 'privileged',
      authenticates_via: 'same session mechanisms, but with roles such as WEBGOAT_ADMIN when present',
    },
    {
      name: 'service-to-service component',
      can_reach:
        'WebWolf mail endpoint, reset-link endpoints, request-trace storage, and WebGoat’s internal companion-service integrations',
      trust_level: 'service',
      authenticates_via:
        'no strong auth at HTTP layer for some WebGoat->WebWolf calls; in practice internal code calls via RestTemplate',
    },
    {
      name: 'internal runtime/file-system context',
      can_reach:
        'local HSQLDB files, per-user lesson schemas, per-user upload directories under ~/.webgoat-<version>/, and WebWolf temp/file-server storage',
      trust_level: 'internal',
      authenticates_via: 'process privileges on the host/container',
    },
  ],
  entry_points: [
    {
      kind: 'http_route',
      name: 'WebGoat login and registration',
      location:
        'src/main/java/org/owasp/webgoat/container/WebSecurityConfig.java:57-92; src/main/java/org/owasp/webgoat/container/users/RegistrationController.java:28-55',
      description:
        'Accepts username/password form posts and OAuth2 login callbacks, creates or loads users, and redirects into the lesson UI.',
      authentication: 'public for login/registration; session established after successful login',
    },
    {
      kind: 'http_route',
      name: 'Lesson shell and lesson pages',
      location:
        'src/main/java/org/owasp/webgoat/container/controller/StartLesson.java:49-66; src/main/resources/webgoat/templates/lesson_content.html:8-11; src/main/resources/webgoat/templates/main_new.html:167-188',
      description: 'Loads lesson HTML/Asciidoctor content and injects lesson-specific model data into the browser UI.',
      authentication: 'authenticated',
    },
    {
      kind: 'http_route',
      name: 'Lesson AJAX/service endpoints',
      location:
        'src/main/java/org/owasp/webgoat/container/service/*.java and many files under src/main/java/org/owasp/webgoat/lessons/**',
      description:
        'Receives request parameters, path variables, and request bodies for hints, progress, resets, challenge submissions, and vulnerable lesson exercises.',
      authentication: 'authenticated unless individually permitAll or lesson-specific',
    },
    {
      kind: 'http_route',
      name: 'Path traversal profile upload/retrieval',
      location: 'src/main/java/org/owasp/webgoat/lessons/pathtraversal/ProfileUpload*.java:30-118',
      description:
        'Accepts multipart uploads and a user-controlled fullName, writes files under a per-user directory, and serves back a profile picture from that directory.',
      authentication: 'authenticated',
    },
    {
      kind: 'http_route',
      name: 'WebWolf file upload/files listing',
      location: 'src/main/java/org/owasp/webgoat/webwolf/FileServer.java:74-135',
      description:
        'Stores uploaded files under a per-user directory and renders a listing that links to files and shows metadata.',
      authentication: 'GET /files is permitAll, POST /fileupload is authenticated by Spring Security',
    },
    {
      kind: 'http_route',
      name: 'WebWolf mailbox and request trace APIs',
      location:
        'src/main/java/org/owasp/webgoat/webwolf/mailbox/MailboxController.java:44-68; src/main/java/org/owasp/webgoat/webwolf/requests/WebWolfTraceRepository.java:41-94',
      description:
        'Accepts arbitrary email JSON into the mailbox store and records incoming HTTP exchanges for later display.',
      authentication: 'GET/POST /mail and trace storage routes are configured permitAll in WebWolf security',
    },
    {
      kind: 'http_route',
      name: 'Companion-service HTTP callbacks',
      location:
        'src/main/java/org/owasp/webgoat/lessons/passwordreset/ResetLinkAssignmentForgotPassword.java:68-117; src/main/java/org/owasp/webgoat/lessons/challenges/challenge7/Assignment7.java:72-95; src/main/java/org/owasp/webgoat/container/WebGoat.java:74-77',
      description:
        'WebGoat uses RestTemplate to call WebWolf endpoints for password-reset/mail exercises and other lesson interactions.',
      authentication: 'internal service calls from application code',
    },
  ],
  trust_boundaries: [
    {
      name: 'browser-to-server boundary',
      crosses: 'form fields, path variables, headers, JSON bodies, multipart files, and cookies',
      description:
        'Untrusted request parameters, cookies, and multipart uploads cross from the learner’s browser into Spring MVC controllers and JSON endpoints.',
    },
    {
      name: 'authenticated-session boundary',
      crosses:
        'session cookies, @CurrentUsername/@CurrentUser injections, user-owned DB rows, and per-user filesystem directories',
      description:
        'A user identity becomes trusted enough to scope files, schemas, progress records, and lesson state once Spring Security populates the session.',
    },
    {
      name: 'WebGoat-to-WebWolf boundary',
      crosses: 'password-reset links, email payloads, and request-trace/mail data',
      description:
        'The two applications communicate over HTTP even though they are part of the same project; WebGoat’s lesson code reaches WebWolf mail/reset endpoints via RestTemplate.',
    },
    {
      name: 'application-to-filesystem boundary',
      crosses: 'uploaded filenames, profile names, reset artifacts, mail/request traces, and per-user directories',
      description:
        'Several lessons and WebWolf components translate request data into on-disk paths and files under user-controlled directories or temp locations.',
    },
    {
      name: 'application-to-database boundary',
      crosses: 'user registration data, progress tracking, lesson initialization, and raw SQL used in lessons',
      description:
        'User and lesson state are stored in HSQLDB via Spring Data JPA/JdbcTemplate/Flyway, so attacker input can affect persistent records and lesson schemas.',
    },
    {
      name: 'application-to-external-identity-provider boundary',
      crosses: 'OIDC/OAuth2 callbacks and identity claims',
      description:
        'OAuth2 login depends on GitHub registration configured in Spring Security; external identity assertions are accepted into the app session.',
    },
  ],
  sensitive_assets: [
    {
      kind: 'credential',
      name: 'user credentials and session state',
      location:
        'src/main/java/org/owasp/webgoat/container/users/WebGoatUser.java; src/main/java/org/owasp/webgoat/container/WebSecurityConfig.java; src/main/java/org/owasp/webgoat/webwolf/WebSecurityConfig.java',
      description:
        'Passwords are stored/handled with NoOpPasswordEncoder, and authenticated session cookies grant access to the lesson environment.',
    },
    {
      kind: 'proprietary_data',
      name: 'per-user lesson progress and solved-state',
      location:
        'src/main/java/org/owasp/webgoat/container/users/UserProgress*.java; src/main/java/org/owasp/webgoat/container/report/ReportCardController.java; src/main/java/org/owasp/webgoat/container/service/LessonMenuService.java',
      description: 'Tracks which lessons/assignments a learner has solved and how many attempts they made.',
    },
    {
      kind: 'infrastructure',
      name: 'local HSQLDB data store and per-user schemas',
      location:
        'src/main/resources/application-webgoat.properties:18-23,36-45; src/main/java/org/owasp/webgoat/container/users/UserService.java:51-54',
      description:
        'Contains user records, per-user schemas, and lesson data. Compromise can affect persistence and lesson initialization.',
    },
    {
      kind: 'proprietary_data',
      name: 'lesson uploads and filesystem state',
      location:
        'src/main/java/org/owasp/webgoat/lessons/pathtraversal/ProfileUploadBase.java:38-118; src/main/java/org/owasp/webgoat/webwolf/FileServer.java:83-135',
      description:
        'Uploaded files and profile images are written to the host/container filesystem under user-specific paths.',
    },
    {
      kind: 'proprietary_data',
      name: 'WebWolf mail and request traces',
      location:
        'src/main/java/org/owasp/webgoat/webwolf/mailbox/MailboxController.java; src/main/java/org/owasp/webgoat/webwolf/requests/WebWolfTraceRepository.java',
      description: 'Contains lesson artifacts, email bodies, and captured HTTP requests/responses used by lessons.',
    },
    {
      kind: 'secret',
      name: 'OAuth2 client secret and keystore material',
      location: 'src/main/resources/application-webgoat.properties:12-16,74-75; src/main/resources/goatkeystore.pkcs12',
      description:
        'TLS keystore and OAuth client secret are checked into config/resource locations with defaults; exposure weakens transport/auth posture.',
    },
    {
      kind: 'proprietary_data',
      name: 'Feedback/hint content and lesson text',
      location:
        'src/main/resources/webgoat/templates/main_new.html:184-188; src/main/resources/webwolf/templates/mailbox.html:124; src/main/resources/webwolf/templates/requests.html:39-47',
      description:
        'Rendered with unescaped Thymeleaf in some places, so malicious lesson content or stored messages can influence the browser DOM.',
    },
  ],
  security_controls: [
    {
      kind: 'authentication',
      name: 'Route-based Spring Security authentication',
      scope:
        'global default-on for non-permitAll routes; coarse route allowlists for static/public paths and a few lesson/service endpoints',
      location:
        'src/main/java/org/owasp/webgoat/container/WebSecurityConfig.java:57-92; src/main/java/org/owasp/webgoat/webwolf/WebSecurityConfig.java:49-82',
      mitigates: ['unauthenticated access', 'some accidental exposure of internal routes'],
    },
    {
      kind: 'other',
      name: 'Session invalidation on logout',
      scope: 'global for each app',
      location:
        'src/main/java/org/owasp/webgoat/container/WebSecurityConfig.java:86; src/main/java/org/owasp/webgoat/webwolf/WebSecurityConfig.java:78',
      mitigates: ['session fixation/persistence after logout'],
    },
    {
      kind: 'other',
      name: 'AJAX-aware authentication entry point',
      scope: 'applies to unauthenticated requests caught by Spring Security',
      location: 'src/main/java/org/owasp/webgoat/container/AjaxAuthenticationEntryPoint.java:48-55',
      mitigates: ['confusing redirects for XHR clients'],
    },
    {
      kind: 'transport_security',
      name: 'Localhost default bind',
      scope: 'default deployment posture',
      location: 'src/main/resources/application-webgoat.properties:3-8,45-50; README.md:20-23,43-45,137-145',
      mitigates: ['accidental internet exposure'],
    },
    {
      kind: 'output_encoding',
      name: 'Thymeleaf output encoding via th:text',
      scope: 'per-binding where th:text is used',
      location:
        'src/main/resources/webgoat/templates/main_new.html:57-67,81-116,138-147,176-181; many templates use th:text',
      mitigates: ['XSS in encoded fields'],
    },
    {
      kind: 'input_validation',
      name: 'File extension allowlist for profile picture retrieval',
      scope: 'per-profile-picture retrieval path',
      location: 'src/main/java/org/owasp/webgoat/lessons/pathtraversal/ProfileUploadBase.java:92-111',
      mitigates: ['some arbitrary file retrieval through the profile viewer'],
    },
    {
      kind: 'input_validation',
      name: 'Path traversal mitigation in fixed lesson variant',
      scope: 'opt-in fixed lesson variant only',
      location: 'src/main/java/org/owasp/webgoat/lessons/pathtraversal/ProfileUploadFix.java:35-40',
      mitigates: ['simple ../ traversal in that lesson'],
    },
    {
      kind: 'other',
      name: 'Temporary request-trace exclusions',
      scope: 'WebWolf tracing only',
      location: 'src/main/java/org/owasp/webgoat/webwolf/requests/WebWolfTraceRepository.java:66-77',
      mitigates: ['noise and some self-referential trace spam'],
    },
    {
      kind: 'parameterized_queries',
      name: 'Spring Data JPA repositories',
      scope: 'for repository-backed lookups/inserts',
      location: 'src/main/java/org/owasp/webgoat/container/users/UserRepository.java; UserProgressRepository.java',
      mitigates: ['SQL injection in those repository methods'],
    },
  ],
  vulnerability_class_impacts: [
    {
      vuln_class: 'Cross-site Scripting (XSS)',
      relevance: 'present',
      blast_radius:
        'Session hijack within the learner’s browser, UI redress inside the lesson environment, theft of lesson state or account actions, and possible pivoting into WebWolf if the browser is already authenticated there.',
      reachability:
        'Request data, lesson content, stored mailbox/request-trace content, and model attributes can flow into templates. Some templates deliberately use th:utext, which bypasses HTML escaping, and several lesson pages/feedback messages are intentionally attacker-influenced.',
      mitigating_controls:
        'Thymeleaf auto-escaping when th:text is used; no CSP/HTTP security headers because WebGoat disables headers; some views are static or use safer bindings.',
      verify:
        'Check whether the sink is th:utext/innerHTML/raw response body or a safe escaped binding, whether the attacker controls the stored value or only reflects it, and whether the rendered page is protected by any CSP that actually applies (none found in config).',
    },
    {
      vuln_class: 'Cross-Site Request Forgery (CSRF)',
      relevance: 'present',
      blast_radius:
        'Forced lesson completion, password-reset/mail actions, file uploads, mailbox deletion, lesson restarts/resets, and any state change reachable by the victim’s authenticated browser session.',
      reachability:
        'CSRF is explicitly disabled in both WebGoat and WebWolf, and many state-changing routes are POST/GET endpoints that rely only on session cookies.',
      mitigating_controls:
        'None found for CSRF; logout does invalidate sessions, but that does not prevent request forgery while logged in.',
      verify:
        'Confirm whether a finding is on a state-changing route and whether it requires only the victim’s session cookie. If yes, the lack of CSRF protection is material and should usually raise severity.',
    },
    {
      vuln_class: 'Broken Access Control / IDOR',
      relevance: 'present',
      blast_radius:
        'Leakage or modification of another learner’s progress, mailbox, files, report card, or lesson state; possible admin-only lesson bypasses depending on the route.',
      reachability:
        'Authorization is mostly route-level; many endpoints use @CurrentUsername/@CurrentUser for scoping but there is little evidence of per-resource authorization checks beyond lesson-specific logic. Any endpoint that accepts a user-supplied identifier, lesson name, or username may be reachable across user boundaries if checks are missing.',
      mitigating_controls:
        'Spring Security authenticated() on non-public routes; some methods derive the current user from the security context rather than request parameters.',
      verify:
        'Check whether the endpoint authorizes by the current principal or merely by a request parameter/path segment. Verify if access is restricted by role, by ownership, or not at all; downgrade only if the object is explicitly bound to the authenticated username.',
    },
    {
      vuln_class: 'SQL Injection',
      relevance: 'present',
      blast_radius:
        'Disclosure or modification of the local HSQLDB lesson data, user records, and per-user schema state; in some cases, lesson progress or credentials could be manipulated.',
      reachability:
        'Repository-backed lookups are parameterized by Spring Data JPA, but lesson code also uses raw JDBC/string concatenation in some exercises. Any request parameter that reaches a raw SQL sink is highly reachable because lessons are authenticated but broadly available.',
      mitigating_controls:
        'Parameterized repositories for some paths; open-in-view disabled; however raw JDBC in lesson code is not protected by the ORM layer.',
      verify:
        'Confirm whether the code uses repository methods/PreparedStatement placeholders versus string concatenation. If a raw SQL sink is in an authenticated lesson endpoint, impact is usually at least user-data compromise in the local database.',
    },
    {
      vuln_class: 'Path Traversal / Arbitrary File Write-Read',
      relevance: 'present',
      blast_radius:
        'Overwrite or read files under the WebGoat/WebWolf data directories, poison lesson artifacts, leak stored uploads, and potentially impact other lessons or user-specific state depending on path normalization and directory handling.',
      reachability:
        'Multipart uploads and filename/fullName parameters are used to create files under per-user directories. ProfileUploadBase writes directly to a File constructed from user-controlled fullName, and WebWolf stores uploaded files based on multipart original filename.',
      mitigating_controls:
        'Some retrieval code filters image extensions; a fixed lesson variant strips ../; per-user directories constrain some writes; no universal path normalization is evident.',
      verify:
        'Check whether the final path is canonicalized and compared against an expected base directory before write/read. Distinguish between the intentionally vulnerable lesson variants and platform code that should be safe.',
    },
    {
      vuln_class: 'SSRF / Outbound HTTP Request Forgery',
      relevance: 'present',
      blast_radius:
        'Access to internal-only services reachable from the container/host, exfiltration of metadata or local web endpoints, and interaction with WebWolf or other localhost-bound services.',
      reachability:
        'WebGoat uses RestTemplate and java.net.URL.openStream() in lesson code; some exercises intentionally accept URL-like input. SSRF is especially relevant when attacker input can influence the destination or scheme.',
      mitigating_controls:
        'Some lessons apply regex allowlists or fixed destinations; default deployment binds localhost, which reduces external exposure but not internal SSRF from the app to localhost.',
      verify:
        'Check whether the code validates the destination by exact allowlist versus a weak regex, whether redirects are followed, and whether the URL is user-controlled or derived from a fixed template. Also verify whether the target network includes loopback/internal-only services.',
    },
    {
      vuln_class: 'Authentication Weakness / Credential Exposure',
      relevance: 'present',
      blast_radius:
        'Account compromise within the training environment, misuse of session-authenticated actions, and exposure of companion-service features if secrets are reused in a non-demo deployment.',
      reachability:
        'Registration and login are public, passwords are stored/handled with NoOpPasswordEncoder, and some lesson flows use very weak or predictable credentials by design. OAuth2 client secrets and a TLS keystore password are checked in with defaults.',
      mitigating_controls:
        'Spring Security session-based auth, logout cookie deletion, and the fact that this is a training app with intentionally weak auth flows.',
      verify:
        'Check whether the specific finding is in the real platform auth path or in a lesson demo. For a true platform issue, assess whether the credential is default, hard-coded, or trivially reversible; for a lesson, downgrade to demo-only impact.',
    },
    {
      vuln_class: 'Information Disclosure',
      relevance: 'present',
      blast_radius:
        'Leakage of stack traces, local file paths, environment/config properties, request traces, lesson titles, and solved-state metadata.',
      reachability:
        'server.error.include-stacktrace=always, actuator env/configprops exposure, public report/menu/trace endpoints, and unescaped rendering of stored content can expose internal details. Some endpoints explicitly return filesystem paths or other debug data.',
      mitigating_controls:
        'Default localhost bind and route authentication reduce who can reach the data, but do not hide it from authenticated users or local callers.',
      verify:
        'Check whether the endpoint is public or authenticated, whether the data includes secrets versus merely debug information, and whether the exposure is a deliberate lesson artifact or an accidental disclosure.',
    },
    {
      vuln_class: 'Insecure Deserialization / Unsafe Object Handling',
      relevance: 'likely',
      blast_radius:
        'Potential RCE or data corruption if a true unsafe Java deserialization sink exists; otherwise it may be limited to lesson-scope state manipulation.',
      reachability:
        'The repository contains a dedicated deserialization lesson category, and Java/Spring applications often deserialize request bodies, session objects, or lesson artifacts. Actual reachability depends on whether a given sink uses native Java serialization, JSON binding, or a library with gadget risk.',
      mitigating_controls:
        'No broad deserialization hardening was confirmed in the code read so far; many flows use JSON/Jackson rather than native serialization.',
      verify:
        'Confirm the sink type. If it is only Jackson/DTO binding, downgrade from native-deserialization severity; if it is java.io.ObjectInputStream or equivalent on attacker-controlled bytes, treat as high risk.',
    },
  ],
  data_flow_notes:
    'Untrusted browser input enters mostly as form parameters, path variables, JSON bodies, multipart files, and request headers. Controllers then route that data into one of a few sinks: persistence (JPA/JdbcTemplate/Flyway), filesystem writes/reads under per-user directories, outbound HTTP requests via RestTemplate/URL openStream, or HTML/JSON responses rendered to the browser; several templates use th:utext, so server-side content can cross into the DOM without escaping.',
  dependency_notes: [
    {
      name: 'Spring Boot / Spring Security',
      reason:
        'Defines the application’s authentication, session management, and route protection baseline; many findings are downgraded or upgraded depending on whether they bypass these filters.',
      location:
        'pom.xml; src/main/java/org/owasp/webgoat/container/WebSecurityConfig.java; src/main/java/org/owasp/webgoat/webwolf/WebSecurityConfig.java',
    },
    {
      name: 'Spring MVC / Thymeleaf',
      reason:
        'Determines how request data becomes model data and HTML. The presence of th:utext in some templates materially affects XSS adjudication.',
      location: 'pom.xml; src/main/resources/webgoat/templates/*.html; src/main/resources/webwolf/templates/*.html',
    },
    {
      name: 'Spring Data JPA / JdbcTemplate / Flyway',
      reason:
        'Shapes the persistence attack surface and whether a bug reaches persistent data, per-user schemas, or migration scripts.',
      location:
        'pom.xml; src/main/java/org/owasp/webgoat/container/users/UserService.java; repositories under src/main/java/org/owasp/webgoat/container/users',
    },
    {
      name: 'RestTemplate',
      reason:
        'Used for intra-app HTTP callbacks to WebWolf and lesson-specific external requests, which makes SSRF-style findings relevant where URLs are attacker-controlled.',
      location:
        'pom.xml; src/main/java/org/owasp/webgoat/container/WebGoat.java; src/main/java/org/owasp/webgoat/lessons/passwordreset/*.java; src/main/java/org/owasp/webgoat/lessons/challenges/challenge7/Assignment7.java',
    },
    {
      name: 'HSQLDB',
      reason:
        'Local embedded database; compromise of queries or schema manipulation affects on-disk state in the user’s home directory / container filesystem.',
      location:
        'pom.xml; src/main/resources/application-webgoat.properties; src/main/resources/application-webwolf.properties',
    },
    {
      name: 'Asciidoctor / lesson template resolvers',
      reason:
        'Lesson content is assembled dynamically from resource files and Asciidoctor text, so output encoding and stored content controls matter when adjudicating XSS/content injection.',
      location:
        'src/main/java/org/owasp/webgoat/container/MvcConfiguration.java; src/main/java/org/owasp/webgoat/container/AsciiDoctorTemplateResolver.java',
    },
    {
      name: 'jQuery/RequireJS/Backbone',
      reason:
        'Client-side rendering and AJAX requests mean DOM-based issues and browser-side trust boundaries are relevant, especially where the UI writes unescaped server data into the DOM.',
      location:
        'src/main/resources/webgoat/templates/main_new.html; src/main/resources/webgoat/static/js/goatApp/goatApp.js',
    },
  ],
  authentication_model:
    'Primarily session-based web authentication. WebGoat and WebWolf both support form login with username/password, OAuth2 login, and session cookie logout cleanup; lessons then use @CurrentUsername/@CurrentUser to obtain the authenticated principal from Spring Security. The project also has special lesson-only authentication/cookie flows (for example hijack-session exercises) that are intentionally weak and should not be treated as production controls.',
  authorization_model:
    'Coarse route authorization enforced by Spring Security is the main model: static assets and a handful of public routes are permitAll, while the rest require authentication. Per-resource authorization is mostly lesson-specific and implemented ad hoc in controllers using the current authenticated username or role checks, so adjudicators should look carefully for ownership checks rather than assuming a framework-level policy.',
  attack_surface_summary:
    'The largest attack surface is authenticated browser traffic into many lesson endpoints plus a smaller set of public routes for login, registration, and static assets. CSRF is disabled and global security headers are disabled in WebGoat, while WebWolf also disables CSRF and exposes public GET/POST routes for files, mail, and traces. The codebase intentionally contains insecure lesson implementations, so many input-to-sink paths are reachable by ordinary authenticated users, and some companion-service routes are open to unauthenticated callers.',
  assumptions: [
    'I assumed the default deployment posture described in README/application properties (localhost-bound) unless Docker/override configuration is used.',
    'I assumed the security posture visible in the open code is representative; I did not inspect every lesson controller, so some lesson-specific controls may exist elsewhere.',
    'I treated the training/demo lesson code as part of the threat surface because it is what users actually interact with, but findings inside obviously intentional exercises should be judged as lesson-scope unless they affect the platform itself.',
  ],
}

/**
 * A deliberately small threat model — one or two of each collection — for stories and unit tests that
 * need to assert on specific rendered content without scanning the full WebGoat document.
 */
export const minimalThreatModelContent: ThreatModelContent = {
  system_overview:
    'Acme Storefront is a customer-facing commerce app backed by a REST API, a relational database, and a third-party payment provider.',
  exposure: {
    notes: 'Publicly reachable behind a WAF; the admin console is IP-allowlisted.',
    internet_facing: 'yes',
    network_position: 'public edge behind a CDN and WAF',
    deployment_environment: 'Kubernetes cluster on a managed cloud provider',
  },
  actors: [
    {
      name: 'anonymous shopper',
      can_reach: 'catalog browsing, search, login, and registration',
      trust_level: 'anonymous',
      authenticates_via: 'none',
    },
    {
      name: 'support administrator',
      can_reach: 'the internal admin console and order-override tooling',
      trust_level: 'privileged',
      authenticates_via: 'SSO with a hardware second factor',
    },
  ],
  entry_points: [
    {
      kind: 'http_route',
      name: 'Checkout API',
      location: 'src/api/checkout.ts:40-120',
      description: 'Accepts cart submissions and initiates payment authorization.',
      authentication: 'authenticated session cookie',
    },
  ],
  trust_boundaries: [
    {
      name: 'browser-to-server boundary',
      crosses: 'form fields, JSON bodies, and cookies',
      description: 'Untrusted browser input crosses into the storefront API.',
    },
  ],
  sensitive_assets: [
    {
      kind: 'credential',
      name: 'customer session tokens',
      location: 'src/auth/session.ts',
      description: 'Session cookies that grant access to a customer account.',
    },
  ],
  security_controls: [
    {
      kind: 'input_validation',
      name: 'Request schema validation',
      scope: 'all JSON API routes',
      location: 'src/api/middleware/validate.ts',
      mitigates: ['injection via malformed payloads', 'mass assignment'],
    },
  ],
  vulnerability_class_impacts: [
    {
      vuln_class: 'SQL Injection',
      relevance: 'present',
      blast_radius: 'Full read/write access to the orders database.',
      reachability: 'Order lookups build queries by string concatenation on a public route.',
      mitigating_controls: 'Parameterized queries are used elsewhere but not on the legacy lookup path.',
      verify: 'Confirm whether the lookup path binds parameters or concatenates request input.',
    },
    {
      vuln_class: 'Cross-Site Request Forgery (CSRF)',
      relevance: 'likely',
      blast_radius: 'Forced state changes on the victim’s authenticated session.',
      reachability: 'State-changing POST routes rely only on the session cookie.',
      mitigating_controls: 'No CSRF tokens were found on the checkout routes.',
      verify: 'Check whether state-changing routes require an anti-CSRF token.',
    },
  ],
  data_flow_notes:
    'Untrusted browser input enters as form parameters and JSON bodies, flows through the storefront API into the orders database, and out to the payment provider.',
  dependency_notes: [
    {
      name: 'Express',
      reason: 'Defines routing and middleware, including where request data becomes handler input.',
      location: 'package.json; src/api/server.ts',
    },
  ],
  authentication_model: 'Session-based web authentication with an SSO option for staff.',
  authorization_model: 'Route-level authorization with per-resource ownership checks in handlers.',
  attack_surface_summary:
    'The largest attack surface is authenticated customer traffic into the checkout and account APIs, plus a small set of public catalog and auth routes.',
  assumptions: ['Assumed the WAF terminates TLS and that edge-to-service traffic is on a private network.'],
}

/**
 * A document from a generator that runs the linking pass (analysis-service#2927): every graph entity
 * carries a stable `id`, and `actors[].reaches`, `entry_points[].exposes` and
 * `vulnerability_class_impacts[].affects` name those ids to state the actor→entry, entry→weakness and
 * weakness→asset edges, and `security_controls[].applies_to` names the entry points each control guards
 * (left out of the Payment webhook receiver and Login on purpose, so the unguarded case is shown too).
 * With them, Attack paths draws all five columns; `exampleThreatModelContent`
 * and `minimalThreatModelContent` omit the fields (as every stored document does today) and so render
 * the two-column control→weakness view instead. Compact and hand-authored: this is the one place the
 * full-picture example lives now that the view derives every edge from the payload.
 */
export const fullThreatModelContent: ThreatModelContent = {
  system_overview:
    'Acme Storefront is a customer-facing commerce app: a browser client, a REST API, a relational database, and a third-party payment provider reached over an outbound webhook.',
  exposure: {
    notes: 'Publicly reachable behind a WAF; the admin console is IP-allowlisted; the webhook receiver is public.',
    internet_facing: 'yes',
    network_position: 'public edge behind a CDN and WAF',
    deployment_environment: 'Kubernetes cluster on a managed cloud provider',
  },
  actors: [
    {
      id: 'act-anon',
      name: 'anonymous shopper',
      can_reach: 'catalog browsing, search, login, and registration',
      trust_level: 'anonymous',
      authenticates_via: 'none',
      reaches: ['ep-catalog', 'ep-auth'],
    },
    {
      id: 'act-customer',
      name: 'authenticated customer',
      can_reach: 'checkout, account management, and order history',
      trust_level: 'authenticated',
      authenticates_via: 'session cookie issued at login',
      reaches: ['ep-checkout', 'ep-account'],
    },
    {
      id: 'act-admin',
      name: 'support administrator',
      can_reach: 'the internal admin console and order-override tooling',
      trust_level: 'privileged',
      authenticates_via: 'SSO with a hardware second factor',
      reaches: ['ep-admin'],
    },
    {
      id: 'act-webhook',
      name: 'payment webhook caller',
      can_reach: 'the public payment-notification endpoint',
      trust_level: 'service',
      authenticates_via: 'a shared signing secret on each request',
      reaches: ['ep-webhook'],
    },
  ],
  entry_points: [
    {
      id: 'ep-catalog',
      kind: 'http_route',
      name: 'Catalog & search API',
      location: 'src/api/catalog.ts:20-140',
      description: 'Serves product listings and renders search results, some of which echo the query string.',
      authentication: 'public',
      exposes: ['vc-xss'],
    },
    {
      id: 'ep-auth',
      kind: 'http_route',
      name: 'Login & registration',
      location: 'src/api/auth.ts:30-120',
      description: 'Accepts credentials, creates sessions, and handles password reset.',
      authentication: 'public',
      exposes: ['vc-authn'],
    },
    {
      id: 'ep-checkout',
      kind: 'http_route',
      name: 'Checkout API',
      location: 'src/api/checkout.ts:40-160',
      description: 'Accepts cart submissions and initiates payment authorization.',
      authentication: 'authenticated session cookie',
      exposes: ['vc-sqli', 'vc-csrf'],
    },
    {
      id: 'ep-account',
      kind: 'http_route',
      name: 'Account & orders API',
      location: 'src/api/account.ts:25-190',
      description: 'Reads and updates the current customer’s profile and orders by id.',
      authentication: 'authenticated session cookie',
      exposes: ['vc-idor'],
    },
    {
      id: 'ep-admin',
      kind: 'http_route',
      name: 'Admin console',
      location: 'src/admin/routes.ts:15-210',
      description: 'Support tooling for looking up and overriding any customer’s orders.',
      authentication: 'SSO, IP-allowlisted',
      exposes: ['vc-idor'],
    },
    {
      id: 'ep-webhook',
      kind: 'http_route',
      name: 'Payment webhook receiver',
      location: 'src/api/webhooks/payment.ts:18-96',
      description: 'Receives payment-status callbacks and fetches a receipt from a URL named in the payload.',
      authentication: 'shared signing secret',
      exposes: ['vc-ssrf'],
    },
  ],
  trust_boundaries: [
    {
      name: 'browser-to-server boundary',
      crosses: 'form fields, JSON bodies, and cookies',
      description: 'Untrusted browser input crosses into the storefront API.',
    },
    {
      name: 'server-to-payment-provider boundary',
      crosses: 'webhook callbacks and receipt URLs',
      description: 'The payment provider calls back into the storefront, and the storefront fetches receipts it names.',
    },
  ],
  sensitive_assets: [
    {
      id: 'as-session',
      kind: 'credential',
      name: 'customer session tokens',
      location: 'src/auth/session.ts',
      description: 'Session cookies that grant access to a customer account.',
    },
    {
      id: 'as-orders',
      kind: 'proprietary_data',
      name: 'orders database',
      location: 'src/db/orders.ts',
      description: 'Every customer’s order history, line items, and fulfilment state.',
    },
    {
      id: 'as-pii',
      kind: 'proprietary_data',
      name: 'customer PII store',
      location: 'src/db/customers.ts',
      description: 'Names, addresses, and contact details for every registered customer.',
    },
    {
      id: 'as-internal',
      kind: 'infrastructure',
      name: 'internal service network',
      location: 'infra/network.tf',
      description: 'Cluster-internal services reachable only from within the private network.',
    },
  ],
  security_controls: [
    {
      id: 'sc-encoding',
      applies_to: ['ep-catalog'],
      kind: 'output_encoding',
      name: 'Output encoding on all rendered fields',
      scope: 'every server-rendered response',
      location: 'src/render/escape.ts',
      mitigates: ['XSS'],
    },
    {
      id: 'sc-params',
      applies_to: ['ep-checkout', 'ep-account', 'ep-admin'],
      kind: 'parameterized_queries',
      name: 'Parameterized queries',
      scope: 'all database access',
      location: 'src/db/client.ts',
      mitigates: ['SQL injection'],
    },
    {
      id: 'sc-csrf',
      applies_to: ['ep-checkout', 'ep-account'],
      kind: 'other',
      name: 'Anti-CSRF tokens on state changes',
      scope: 'all state-changing routes',
      location: 'src/api/middleware/csrf.ts',
      mitigates: ['CSRF'],
    },
    {
      id: 'sc-authz',
      applies_to: ['ep-account', 'ep-admin'],
      kind: 'other',
      name: 'Central authorization checks',
      scope: 'account and admin routes',
      location: 'src/api/middleware/authorize.ts',
      mitigates: ['broken access control'],
    },
  ],
  vulnerability_class_impacts: [
    {
      id: 'vc-xss',
      affects: ['as-session'],
      vuln_class: 'Cross-site Scripting (XSS)',
      relevance: 'present',
      blast_radius: 'Session hijack within the shopper’s browser and actions taken as the victim.',
      reachability: 'Search results echo the query string into the page.',
      mitigating_controls: 'Output encoding is applied to rendered fields.',
      verify: 'Confirm whether the echoed value is encoded or written as raw HTML.',
    },
    {
      id: 'vc-sqli',
      affects: ['as-orders', 'as-pii'],
      vuln_class: 'SQL Injection',
      relevance: 'present',
      blast_radius: 'Read/write access to the orders database and customer PII.',
      reachability: 'A legacy order-lookup path builds a query by string concatenation.',
      mitigating_controls: 'Parameterized queries are used on the primary data-access path.',
      verify: 'Confirm whether the lookup path binds parameters or concatenates request input.',
    },
    {
      id: 'vc-csrf',
      affects: ['as-orders'],
      vuln_class: 'Cross-Site Request Forgery (CSRF)',
      relevance: 'likely',
      blast_radius: 'Forced state changes on the victim’s authenticated session.',
      reachability: 'State-changing checkout routes rely on the session cookie.',
      mitigating_controls: 'Anti-CSRF tokens are required on state-changing routes.',
      verify: 'Check whether the token is validated on every state-changing route.',
    },
    {
      id: 'vc-idor',
      affects: ['as-orders', 'as-pii'],
      vuln_class: 'Broken Access Control / IDOR',
      relevance: 'present',
      blast_radius: 'Read or modification of another customer’s orders and PII.',
      reachability: 'Account and admin routes accept an order id and look it up directly.',
      mitigating_controls: 'Central authorization checks scope lookups to the current principal.',
      verify: 'Check whether the route authorizes by the current principal or merely by the id in the request.',
    },
    {
      id: 'vc-authn',
      affects: ['as-session'],
      vuln_class: 'Authentication Weakness / Credential Exposure',
      relevance: 'present',
      blast_radius: 'Account compromise and misuse of session-authenticated actions.',
      reachability: 'Password reset and login are public and rate-limited only at the edge.',
      mitigating_controls: 'None specific to credential strength were found on these routes.',
      verify: 'Assess whether credentials are hashed with a strong algorithm and whether reset tokens expire.',
    },
    {
      id: 'vc-ssrf',
      affects: ['as-internal'],
      vuln_class: 'SSRF / Outbound HTTP Request Forgery',
      relevance: 'likely',
      blast_radius: 'Access to internal-only services reachable from the cluster network.',
      reachability: 'The webhook receiver fetches a receipt from a URL named in the request payload.',
      mitigating_controls: 'No allowlist constrains the fetched destination.',
      verify: 'Check whether the destination is validated against an allowlist or can name loopback/internal hosts.',
    },
  ],
  data_flow_notes:
    'Untrusted browser input enters as form parameters and JSON bodies, flows through the storefront API into the orders and customer databases, and out to the payment provider over an outbound webhook fetch.',
  dependency_notes: [
    {
      name: 'Express',
      reason: 'Defines routing and middleware, including where request data becomes handler input.',
      location: 'package.json; src/api/server.ts',
    },
  ],
  authentication_model: 'Session-based web authentication with an SSO option for staff.',
  authorization_model: 'Route-level authorization with per-resource ownership checks in handlers.',
  attack_surface_summary:
    'Public catalog, auth, and webhook routes plus authenticated checkout and account APIs; the admin console is IP-allowlisted.',
  assumptions: ['Assumed the WAF terminates TLS and that edge-to-service traffic is on a private network.'],
}
